import { useState, useEffect } from 'react';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

const sectionHeading = (first) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--rasp-teal-light)',
  marginBottom: 12,
  marginTop: first ? 0 : 32,
});

function relativeTime(iso) {
  if (!iso) return null;
  const date = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(Math.abs(diffMs) / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function AnalyticsView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/analytics/associate`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`Request failed: ${r.status}`);
        return r.json();
      })
      .then(json => { setData(json); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  if (loading) {
    return (
      <div style={{ padding: 24, color: 'var(--rasp-fg-2)', fontSize: 13 }}>Loading...</div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: 'var(--rasp-danger)', fontSize: 13 }}>{error}</div>
    );
  }

  const portfolio = data?.portfolio || [];

  const rollupCards = [
    { label: 'Active Projects',      value: data.rollup.active_projects ?? 0 },
    { label: 'Confirmed (This Week)', value: data.rollup.confirmed_week  ?? 0 },
    { label: 'Pending Pitches',      value: data.rollup.pending_pitches  ?? 0 },
    { label: 'Stuck Engaged',        value: data.rollup.stuck_engaged    ?? 0 },
  ];

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>

        {/* Section 1: Attention Queue */}
        <div style={sectionHeading(true)}>Needs Attention</div>
        <div style={{ background: 'var(--rasp-surface)', border: '1px solid var(--rasp-border)', borderRadius: 8, padding: '16px 20px' }}>
          {(() => {
            const attention = data?.attention || [];
            if (attention.length === 0) {
              return <div style={{ fontSize: 13, color: 'var(--rasp-fg-2)' }}>No flags.</div>;
            }
            return attention.map((item, idx) => {
              const isLast = idx === attention.length - 1;
              return (
                <div key={`${item.project_id}-${item.signal}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: isLast ? undefined : '1px solid var(--rasp-border-soft)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--rasp-fg-1)', marginBottom: 4 }}>{item.project_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--rasp-fg-2)' }}>{item.reason}</div>
                  </div>
                  <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '1px solid var(--rasp-border-strong)', background: 'transparent', color: 'var(--rasp-fg-2)', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 16 }}>
                    {item.cta}
                  </button>
                </div>
              );
            });
          })()}
        </div>

        {/* Section 2: Rollup Cards */}
        <div style={sectionHeading(false)}>Rollup</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {rollupCards.map(card => (
            <div key={card.label} style={{ background: 'var(--rasp-surface)', border: '1px solid var(--rasp-border)', borderRadius: 8, padding: '20px 24px' }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--rasp-fg-3)', marginBottom: 8 }}>{card.label}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--rasp-fg-1)', lineHeight: 1 }}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* Section 3: Portfolio Table */}
        <div style={sectionHeading(false)}>All Projects</div>
        <div style={{ background: 'var(--rasp-surface)', border: '1px solid var(--rasp-border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rasp-fg-3)', borderBottom: '1px solid var(--rasp-border)', fontWeight: 500 }}>Project</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rasp-fg-3)', borderBottom: '1px solid var(--rasp-border)', fontWeight: 500 }}>Client</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rasp-fg-3)', borderBottom: '1px solid var(--rasp-border)', fontWeight: 500 }}>Confirmed</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rasp-fg-3)', borderBottom: '1px solid var(--rasp-border)', fontWeight: 500 }}>In Flight</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rasp-fg-3)', borderBottom: '1px solid var(--rasp-border)', fontWeight: 500 }}>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {portfolio.map((p, idx) => {
                const isLast = idx === portfolio.length - 1;
                const rowBorder = isLast ? undefined : '1px solid var(--rasp-border-soft)';
                return (
                  <tr key={p.id}>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 500, color: 'var(--rasp-fg-1)', borderBottom: rowBorder }}>{p.name}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--rasp-fg-2)', borderBottom: rowBorder }}>{p.client}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: p.confirmed > 0 ? 'var(--rasp-teal-light)' : 'var(--rasp-fg-2)', borderBottom: rowBorder }}>{p.confirmed}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, fontFamily: 'var(--rasp-mono)', color: 'var(--rasp-fg-2)', borderBottom: rowBorder }}>
                      {p.pending} · {p.pitched} · {p.engaged}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--rasp-fg-2)', fontFamily: 'var(--rasp-mono)', borderBottom: rowBorder }}>
                      {p.last_active ? relativeTime(p.last_active) : '—'}
                    </td>
                  </tr>
                );
              })}
              {portfolio.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '20px 16px', color: 'var(--rasp-fg-2)', fontSize: 13, textAlign: 'center' }}>
                    No projects found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
